/**
 * Network-aware utilities for optimizing video loading based on connection quality
 * Implements the Network Information API to detect slow connections and disable
 * autoplay videos on 2G/3G networks to improve PageSpeed Insights scores
 */

import React from 'react';

export type ConnectionType = '4g' | '3g' | '2g' | 'slow-2g' | 'unknown';

export interface NetworkInfo {
  effectiveType: ConnectionType;
  downlink: number; // Mbps
  saveData: boolean;
  rtt: number; // Round-trip time in ms
}

/**
 * Get current network information using the Network Information API
 * Falls back to conservative defaults if API is not available
 */
export function getNetworkInfo(): NetworkInfo {
  if (typeof window === 'undefined' || !('connection' in navigator)) {
    // Conservative default: assume slow connection if API unavailable
    return {
      effectiveType: 'unknown',
      downlink: 0,
      saveData: false,
      rtt: 0,
    };
  }

  const connection = (navigator as any).connection || 
                    (navigator as any).mozConnection || 
                    (navigator as any).webkitConnection;

  if (!connection) {
    return {
      effectiveType: 'unknown',
      downlink: 0,
      saveData: false,
      rtt: 0,
    };
  }

  const effectiveType = (connection.effectiveType || 'unknown') as ConnectionType;
  const downlink = connection.downlink || 0;
  const saveData = connection.saveData || false;
  const rtt = connection.rtt || 0;

  return {
    effectiveType,
    downlink,
    saveData,
    rtt,
  };
}

/**
 * Determine if autoplay videos should be disabled based on network conditions
 * Returns true if videos should be disabled (slow connection or data saver mode)
 */
export function shouldDisableAutoplayVideos(): boolean {
  const networkInfo = getNetworkInfo();

  // Disable if data saver mode is enabled
  if (networkInfo.saveData) {
    return true;
  }

  // Disable on slow connections (2G, slow-2G, or 3G with low bandwidth)
  if (networkInfo.effectiveType === '2g' || networkInfo.effectiveType === 'slow-2g') {
    return true;
  }

  // Disable on 3G if bandwidth is very low (< 1 Mbps)
  if (networkInfo.effectiveType === '3g' && networkInfo.downlink < 1) {
    return true;
  }

  // Disable if effectiveType is unknown and downlink is very low (conservative approach)
  if (networkInfo.effectiveType === 'unknown' && networkInfo.downlink > 0 && networkInfo.downlink < 1) {
    return true;
  }

  return false;
}

/**
 * Get recommended video quality/bitrate based on network conditions
 * Returns 'high', 'medium', 'low', or 'none' (show poster only)
 */
export function getRecommendedVideoQuality(): 'high' | 'medium' | 'low' | 'none' {
  const networkInfo = getNetworkInfo();

  // Show poster only on very slow connections
  if (shouldDisableAutoplayVideos()) {
    return 'none';
  }

  // High quality for 4G with good bandwidth (> 5 Mbps)
  if (networkInfo.effectiveType === '4g' && networkInfo.downlink >= 5) {
    return 'high';
  }

  // Medium quality for 4G with moderate bandwidth (2-5 Mbps) or 3G with good bandwidth (> 2 Mbps)
  if (
    (networkInfo.effectiveType === '4g' && networkInfo.downlink >= 2) ||
    (networkInfo.effectiveType === '3g' && networkInfo.downlink >= 2)
  ) {
    return 'medium';
  }

  // Low quality for 3G with moderate bandwidth (1-2 Mbps)
  if (networkInfo.effectiveType === '3g' && networkInfo.downlink >= 1) {
    return 'low';
  }

  // Default to medium for unknown connections with reasonable bandwidth
  if (networkInfo.downlink >= 2) {
    return 'medium';
  }

  // Default to low for unknown connections
  return 'low';
}

/**
 * Check if the current connection is slow enough to show WebP fallback instead of video
 * Returns true for slow-2g, 2g, and 3g connections
 * This improves PageSpeed Insights scores on mobile by avoiding video downloads on slow networks
 */
export function isSlowConnection(): boolean {
  const networkInfo = getNetworkInfo();
  return ['slow-2g', '2g', '3g'].includes(networkInfo.effectiveType) ||
         (networkInfo.effectiveType === 'unknown' && networkInfo.downlink < 1);
}

/**
 * Hook to monitor network changes and return current network info
 * Useful for reactive components that need to adjust video loading based on connection
 */
export function useNetworkInfo() {
  if (typeof window === 'undefined') {
    return {
      networkInfo: getNetworkInfo(),
      shouldDisableAutoplay: false,
      recommendedQuality: 'medium' as const,
    };
  }

  const [networkInfo, setNetworkInfo] = React.useState<NetworkInfo>(getNetworkInfo);

  React.useEffect(() => {
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;

    if (!connection) {
      return;
    }

    const updateNetworkInfo = () => {
      setNetworkInfo(getNetworkInfo());
    };

    // Listen for network changes
    connection.addEventListener('change', updateNetworkInfo);

    return () => {
      connection.removeEventListener('change', updateNetworkInfo);
    };
  }, []);

  return {
    networkInfo,
    shouldDisableAutoplay: shouldDisableAutoplayVideos(),
    recommendedQuality: getRecommendedVideoQuality(),
  };
}
